/**
 * Scene setup orchestrator - combines all scene components
 */

import type * as THREE_NS from 'three';
import { setupLighting } from './lighting.js';
import { createGridParticles } from './particles.js';
import { createGradientSphere } from './gradientSphere.js';
import { TOWER_CONFIG } from './jsPlacementTowerConfig.js';
import { createCircularRibbon } from './ribbon.js';
import { createPipes, type PipeCurves, type PipeConfig } from './pipes.js';
import { createTestArrow } from './testArrow.js';
import { createJSPlacementTower } from './jsPlacementTower.js';
import { createCortexNetwork, type CortexNetworkState } from './cortexNetwork.js';
import { createScatteredLights, type ScatteredLightsState } from './scatteredLights.js';
import { createMetaball, type MetaballState } from './metaball.js';
import { cortexStatusManager } from '../cortexStatusManager.js';

export interface SceneSetupResult {
  particles: THREE_NS.Points;
  ribbonPlanes: THREE_NS.Mesh[];
  pipes: PipeCurves;
  testArrow: THREE_NS.Group;
  jsPlacementTower: THREE_NS.Group | null;
  gradientSphere: THREE_NS.Mesh | null;
  cortexNetwork: CortexNetworkState;
  scatteredLights: ScatteredLightsState;
  metaball: MetaballState | null;
}

/**
 * Set up the complete 3D scene with all components
 */
export function setupScene(
  scene: THREE_NS.Scene,
  THREE: typeof THREE_NS,
  pipeConfig: PipeConfig,
  curveType: 'bezier' | 'natural-cubic' | 'hermite' | 'b-spline' = 'bezier'
): SceneSetupResult {
  // Setup lighting
  setupLighting(scene, THREE);

  // Create grid particles
  const particles = createGridParticles(scene, THREE);

  // Create gradient sphere (using scale and gradient from config)
  // Validate and clamp the saved index to ensure it's within bounds
  const gradients = TOWER_CONFIG.skyboxGradients;
  const savedIndex = TOWER_CONFIG.activeSkyboxGradientIndex;
  const validIndex = (savedIndex >= 0 && savedIndex < gradients.length) ? savedIndex : 0;
  const activeGradient = gradients[validIndex] || gradients[0];
  const gradientSphere = createGradientSphere(scene, THREE, TOWER_CONFIG.skyboxScale, activeGradient);

  // Initialize sky sphere animation system
  import('./skySphereAnimations.js').then(({ initializeSkySphereAnimations }) => {
    initializeSkySphereAnimations(gradientSphere, activeGradient);
    // Note: Color cycle animation was planned but never implemented
    // The file colorCycleAnimation.js does not exist
  });

  // If the saved index was out of bounds, update it to the valid index
  if (savedIndex !== validIndex) {
    TOWER_CONFIG.activeSkyboxGradientIndex = validIndex;
    try {
      localStorage.setItem('majaku-privacy-skybox-gradient-index', String(validIndex));
    } catch (error) {
      console.warn('[Scene Setup] Failed to save corrected gradient index:', error);
    }
  }

  // Create circular ribbons
  const ribbonPlanes = createCircularRibbon(scene, THREE);

  // Create test arrow at center pointing at camera first
  const testArrow = createTestArrow(scene, THREE);

  // Create pipes
  const pipes = createPipes(scene, THREE, pipeConfig, curveType);

  // Parent pipe groups to the arrow so they automatically follow its rotation
  if (testArrow && pipes.upPipeGroup && pipes.downPipeGroup) {
    // Rotate curves locally - same 90-degree Z-axis rotation as the arrow
    // Up pipe should point up (from ceiling), down pipe should point down (from floor)
    const { Quaternion, Vector3 } = THREE as any;
    const rotationZ = new Quaternion();
    rotationZ.setFromAxisAngle(new Vector3(0, 0, 1), Math.PI / 2); // 90 degrees around Z-axis (same as arrow)

    // Apply Z-axis rotation to both pipes
    pipes.upPipeGroup.quaternion.multiplyQuaternions(rotationZ, pipes.upPipeGroup.quaternion);
    pipes.downPipeGroup.quaternion.multiplyQuaternions(rotationZ, pipes.downPipeGroup.quaternion);

    testArrow.add(pipes.upPipeGroup);
    testArrow.add(pipes.downPipeGroup);
    // Remove from scene since they're now children of arrow
    scene.remove(pipes.upPipeGroup);
    scene.remove(pipes.downPipeGroup);
  }

  // Create JS Placement tower (async, so we'll load it after scene setup)
  let jsPlacementTower: THREE_NS.Group | null = null;
  createJSPlacementTower(scene, THREE).then((tower) => {
    jsPlacementTower = tower;
  }).catch((error) => {
    console.warn('[Scene Setup] Failed to create JS Placement tower:', error);
  });

  // Create cortex network visualization at center
  // Note: renderer and camera will be passed later when available
  const cortexNetwork = createCortexNetwork(scene, THREE);
  
  // Initialize cortex status manager with all cortex IDs
  // Extract cortex IDs from the network state (nodes map keys)
  const cortexIds = Array.from(cortexNetwork.nodes.keys());
  cortexStatusManager.initializeCortices(cortexIds);

  // Create scattered FAA aircraft warning lights throughout the scene
  // Deterministic seed ensures reproducible positions
  // minDistance: 300 (well beyond camera radius ~25), maxDistance: 1500 (for immense scale)
  const scatteredLights = createScatteredLights(scene, THREE, 20, 12345, 300, 1500);

  // Metaball will be created in uiSphereScene instead (for composite rendering)
  const metaball = null;

  return {
    particles,
    ribbonPlanes,
    pipes,
    testArrow,
    jsPlacementTower,
    gradientSphere,
    cortexNetwork,
    scatteredLights,
    metaball,
  };
}
